import { create } from 'zustand';

/**
 * Transient UI state.
 *
 * Design doc §Pitfall 7 insists this NEVER mutate canonical generation state
 * implicitly. The split is strict: changing `storyPeel` or `viewMode` only
 * affects what's rendered or exported (the latter only if the user opts in).
 */

export type ViewMode = 'exterior' | 'topDown' | 'wireframeDebug';
export type ThemeMode = 'dark' | 'light';

export interface Toast {
  id: number;
  message: string;
  tone: 'info' | 'success' | 'warn';
}

/** Info shown under the ego-mode crosshair when a hinge is in reach.
 *  Verb already reflects the next action the player would take, so
 *  the UI doesn't have to look up the current hinge value itself. */
export interface EgoInteractHint {
  id: string;
  label: string;
  verb: 'Open' | 'Close';
}

export interface UiState {
  theme: ThemeMode;
  viewMode: ViewMode;
  autoRotate: boolean;
  /** Peel level: 0=off, 1=roof, 2=roof+top story, …, n+1=roof+all stories. */
  storyPeel: number;
  /** Debug overlays toggle (also controlled by ?debug=1 in the URL). */
  debug: boolean;
  /** Sidebar panel collapsed state. */
  panelCollapsed: boolean;
  /** Sun position: 0 = sunrise, 0.5 = midday, 1 = sunset. */
  sun: number;
  /** When false, the lot base plate, fence and mailbox are hidden so only
   *  the house body itself remains in the scene. */
  showContext: boolean;
  /** First-person walk-around mode. Not persisted — it's a transient
   *  interaction state and should never survive a page refresh (users
   *  expect to land on the orbit view). */
  egoMode: boolean;
  /** What the ego-mode crosshair is pointing at (if anything), or null
   *  when no interactable is in reach. Kept in the store so the React
   *  overlay can render the prompt without a render-loop callback. */
  interactHint: EgoInteractHint | null;
  /** Transient toast messages (auto-expire). */
  toasts: Toast[];

  setTheme(t: ThemeMode): void;
  setViewMode(v: ViewMode): void;
  setAutoRotate(on: boolean): void;
  setStoryPeel(n: number): void;
  setDebug(on: boolean): void;
  setPanelCollapsed(on: boolean): void;
  setSun(t: number): void;
  setShowContext(on: boolean): void;
  setEgoMode(on: boolean): void;
  setInteractHint(hint: EgoInteractHint | null): void;
  pushToast(message: string, tone?: Toast['tone']): void;
  dismissToast(id: number): void;
}

const THEME_KEY = 'structure.theme';
const AUTO_ROTATE_KEY = 'structure.autoRotate';
const PANEL_COLLAPSED_KEY = 'structure.panelCollapsed';
const SUN_KEY = 'structure.sun';
const SHOW_CONTEXT_KEY = 'structure.showContext';

function safeLoad<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function safeStore(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Incognito / sandboxed: fall back silently per design doc §Persistence.
  }
}

function initialTheme(): ThemeMode {
  const saved = safeLoad<ThemeMode | null>(THEME_KEY, null);
  if (saved === 'dark' || saved === 'light') return saved;
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'dark';
}

function initialDebug(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('debug') === '1';
  } catch {
    return false;
  }
}

let nextToastId = 1;

export const useUiStore = create<UiState>((set, get) => ({
  theme: initialTheme(),
  viewMode: 'exterior',
  autoRotate: safeLoad<boolean>(AUTO_ROTATE_KEY, true),
  storyPeel: 0,
  debug: initialDebug(),
  panelCollapsed: safeLoad<boolean>(PANEL_COLLAPSED_KEY, false),
  sun: safeLoad<number>(SUN_KEY, 0.45),
  showContext: safeLoad<boolean>(SHOW_CONTEXT_KEY, true),
  egoMode: false,
  interactHint: null,
  toasts: [],

  setTheme(t) {
    set({ theme: t });
    safeStore(THEME_KEY, t);
  },
  setViewMode(v) {
    set({ viewMode: v });
  },
  setAutoRotate(on) {
    set({ autoRotate: on });
    safeStore(AUTO_ROTATE_KEY, on);
  },
  setStoryPeel(n) {
    set({ storyPeel: Math.max(0, Math.floor(n)) });
  },
  setDebug(on) {
    set({ debug: on });
  },
  setPanelCollapsed(on) {
    set({ panelCollapsed: on });
    safeStore(PANEL_COLLAPSED_KEY, on);
  },
  setSun(t) {
    const v = Math.max(0, Math.min(1, t));
    set({ sun: v });
    safeStore(SUN_KEY, v);
  },
  setShowContext(on) {
    set({ showContext: on });
    safeStore(SHOW_CONTEXT_KEY, on);
  },
  setEgoMode(on) {
    // Clear the interact hint whenever ego mode flips off — nothing
    // should keep displaying "Front Door · [E] Open" once we're back in
    // orbit view.
    set({ egoMode: on, interactHint: on ? get().interactHint : null });
  },
  setInteractHint(hint) {
    set({ interactHint: hint });
  },
  pushToast(message, tone = 'info') {
    const id = nextToastId++;
    const toast: Toast = { id, message, tone };
    set({ toasts: [...get().toasts, toast] });
    if (typeof window !== 'undefined') {
      window.setTimeout(() => {
        set({ toasts: get().toasts.filter((t) => t.id !== id) });
      }, 2000);
    }
  },
  dismissToast(id) {
    set({ toasts: get().toasts.filter((t) => t.id !== id) });
  },
}));
